import 'swiper/css';
import 'swiper/css/effect-coverflow';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import React, { useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, Typography, IconButton } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { api } from "../../config";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, EffectCoverflow } from "swiper/modules";

export default function ProjectCoverflow({ projects = [], onSelect }) {
    const { t } = useTranslation();
    const prevRef = useRef(null);
    const nextRef = useRef(null);

    const getProjectImage = useCallback((project) => {
        if (Array.isArray(project.images) && project.images.length > 0) {
            const hero = project.images.find((i) => i.isHero) || project.images[0];
            const path = hero?.path || "";
            if (!path) return "/placeholder-image.jpg";
            return path.includes(",") ? api(`/Progetti/${path}`) : api(`/Progetti/${project.id}/${path}`);
        }
        if (project.heroImage) {
            const path = project.heroImage;
            return path.includes(",") ? api(`/Progetti/${path}`) : api(`/Progetti/${project.id}/${path}`);
        }
        return "/placeholder-image.jpg";
    }, []);

    return (
        <Box sx={{ position: "relative", width: "100%" }}>
            {/* Frecce custom (MUI) */}
            <IconButton
                ref={prevRef}
                sx={{
                    position: "absolute",
                    left: { xs: 4, sm: 8 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                }}
            >
                <ChevronLeftIcon />
            </IconButton>

            <IconButton
                ref={nextRef}
                sx={{
                    position: "absolute",
                    right: { xs: 4, sm: 8 },
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                    backgroundColor: "rgba(0,0,0,0.4)",
                    color: "#fff",
                    "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                }}
            >
                <ChevronRightIcon />
            </IconButton>

            <Swiper
                modules={[Navigation, Pagination, EffectCoverflow]}
                effect="coverflow"
                coverflowEffect={{
                    rotate: 0,
                    stretch: 0,
                    depth: 180,
                    modifier: 1,
                    slideShadows: false,
                }}
                centeredSlides
                slidesPerView="auto"
                spaceBetween={0}
                grabCursor
                loop={projects.length > 3}
                pagination={{ clickable: true }}
                onBeforeInit={(swiper) => {
                    swiper.params.navigation.prevEl = prevRef.current;
                    swiper.params.navigation.nextEl = nextRef.current;
                }}
                navigation={{
                    prevEl: prevRef.current,
                    nextEl: nextRef.current,
                }}
                speed={500}
                style={{
                    padding: "10px 56px 32px",
                }}
            >

                {projects.map((project) => (
                    <SwiperSlide
                        key={project.id}
                        style={{
                            width: "clamp(220px, 28vw, 360px)",
                            margin: "0 clamp(12px, 2.5vw, 40px)",
                        }}
                    >
                        <Box
                            onClick={() => onSelect?.(project)}
                            sx={{
                                cursor: "pointer",
                                borderRadius: 2,
                                overflow: "hidden",
                                backgroundColor: "#fff",
                                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                                transition: "transform .25s ease, box-shadow .25s ease",
                                position: "relative",
                                "&:hover": {
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 16px 32px rgba(0,0,0,0.18)",
                                },
                            }}
                        >
                            {/* Container immagine con effetto hover */}
                            <Box
                                sx={{
                                    position: "relative",
                                    aspectRatio: "4 / 3",
                                    overflow: "hidden",
                                    '&:hover .projects-text-overlay': {
                                        opacity: 1,
                                    },
                                    '&:hover .projects-image': {
                                        transform: "scale(1.02)",
                                    }
                                }}
                            >
                                {/* Immagine principale */}
                                <Box
                                    component="img"
                                    className="projects-image"
                                    src={`${getProjectImage(project)}?w=800&h=600&fit=crop&auto=format`}
                                    alt={project.titolo || project.citta || `Progetto ${project.id}`}
                                    loading="lazy"
                                    decoding="async"
                                    sx={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                        display: "block",
                                        transition: "transform 0.3s ease-in-out"
                                    }}
                                    onError={(e) => (e.currentTarget.src = "/placeholder-image.jpg")}
                                />

                                {/* Overlay con gradiente (come Progetti) */}
                                <Box
                                    className="projects-text-overlay"
                                    sx={{
                                        position: "absolute",
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        background: "linear-gradient(180deg, rgba(153,25,27,0) 0%, rgba(153,25,27,0.8) 100%)",
                                        opacity: 0,
                                        transition: "opacity 0.3s ease",
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "flex-end",
                                        alignItems: "flex-start",
                                        padding: "1rem",
                                        borderRadius: "8px",
                                        color: "white",
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontFamily: "'Roboto', sans-serif",
                                            fontSize: "1rem",
                                            fontWeight: 700,
                                            margin: "0 0 0.25rem 0",
                                            lineHeight: 1.2,
                                        }}
                                    >
                                        {project.titolo || t('common.untitled')}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontFamily: "'Roboto', sans-serif",
                                            fontSize: "0.85rem",
                                            fontWeight: 300,
                                            margin: 0,
                                            opacity: 0.9,
                                        }}
                                    >
                                        {project.citta || ""}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </SwiperSlide>
                ))}
            </Swiper>
        </Box>
    );
}